/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICommerceApiSettings,
    ICreateActionContext,
    isEmptyOrNullObject
} from '@msdyn365-commerce/core';
import { getCartState, getCheckoutState, ICartState, ICheckoutState } from '@msdyn365-commerce/global-state';
import { CardPaymentAcceptPoint, CartsDataActions } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions, buildCacheKey } from '@msdyn365-commerce-modules/retail-actions';
import { get } from 'lodash';

/**
 * Get card payment accept point input.
 */
export interface IGetCardPaymentAcceptPointInput {
    shouldShowBillingAddress?: boolean;
    paymentTenderType?: string;
    apiSettings: ICommerceApiSettings;
    shouldEnableSinglePaymentAuthorizationCheckout?: boolean;
    amountDue?: number;
}

/**
 * GetCardPaymentAcceptPointInput - Input for create GetCardPaymentAcceptPoint.
 */
export class GetCardPaymentAcceptPointInput implements IActionInput {
    public shouldShowBillingAddress?: boolean;

    public paymentTenderType?: string;

    public shouldEnableSinglePaymentAuthorizationCheckout?: boolean;

    public amountDue?: number;

    private readonly apiSettings: ICommerceApiSettings;

    // eslint-disable-next-line @typescript-eslint/explicit-member-accessibility -- Existing code.
    constructor(input: IGetCardPaymentAcceptPointInput) {
        this.shouldShowBillingAddress = input.shouldShowBillingAddress;
        this.paymentTenderType = input.paymentTenderType;
        this.apiSettings = input.apiSettings;
        this.shouldEnableSinglePaymentAuthorizationCheckout = input.shouldEnableSinglePaymentAuthorizationCheckout;
        this.amountDue = input.amountDue;
    }

    /**
     * Get the cache key.
     * @returns Cache key.
     */
    public getCacheKey = (): string =>
        buildCacheKey(`CardPaymentAcceptPoint${this.paymentTenderType ? `-${this.paymentTenderType}-express` : ''}`, this.apiSettings);

    /**
     * Get cache object type.
     * @returns Card payment accept point.
     */
    public getCacheObjectType = (): string => 'CardPaymentAcceptPoint';

    /**
     * Get data cache type.
     * @returns The cache type.
     */
    public dataCacheType = (): CacheType => 'none';
}

/**
 * Create checkout express input.
 * @param inputData - Get card payment accept point.
 * @returns CardPaymentAcceptInput.
 */
export const createCheckoutPaymentInstrumentInput = (
    inputData: ICreateActionContext<IGetCardPaymentAcceptPointInput>
): GetCardPaymentAcceptPointInput => {
    const { shouldShowBillingAddress, paymentTenderType, shouldEnableSinglePaymentAuthorizationCheckout, amountDue } =
        inputData.config ?? {};
    const getCardPaymentAcceptPointInput = {
        shouldShowBillingAddress,
        paymentTenderType,
        apiSettings: inputData.requestContext.apiSettings,
        shouldEnableSinglePaymentAuthorizationCheckout,
        amountDue
    };

    return new GetCardPaymentAcceptPointInput(getCardPaymentAcceptPointInput);
};

/**
 * Calls the Retail API and returns a cart object based on the passed GetCartInput.
 * @param input - The get card payment accept point.
 * @param ctx - The action context.
 * @returns CardPaymentAcceptPoint.
 */
export async function getCardPaymentAcceptPointAction(
    input: GetCardPaymentAcceptPointInput,
    ctx: IActionContext
): Promise<CardPaymentAcceptPoint> {
    // If no cart ID is provided in input, we need to create a cart object
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Double check.
    if (!input) {
        ctx.telemetry.exception(new Error('[getCardPaymentAcceptPointAction] No valid Input was provided, failing'));
        throw new Error('[getCardPaymentAcceptPointAction] No valid Input was provided, failing');
    }

    const { paymentTenderType, amountDue = 0 } = input;
    const checkoutState: ICheckoutState = await getCheckoutState(ctx).catch((error: Error) => {
        ctx.telemetry.exception(error);
        throw error;
    });

    const cartState: ICartState = await getCartState(ctx).catch((error: Error) => {
        ctx.telemetry.exception(error);
        throw error;
    });

    if (
        isEmptyOrNullObject(checkoutState) ||
        isEmptyOrNullObject(checkoutState.checkoutCart) ||
        isEmptyOrNullObject(checkoutState.checkoutCart.cart)
    ) {
        ctx.telemetry.exception(new Error('[getCardPaymentAcceptPointAction] Unable to get checkoutState cart'));
        throw new Error('[getCardPaymentAcceptPointAction] Unable to get checkoutState cart');
    }

    if (isEmptyOrNullObject(cartState) || isEmptyOrNullObject(cartState.cart)) {
        ctx.telemetry.exception(new Error('[getCardPaymentAcceptPointAction] Unable to get cartState cart'));
        throw new Error('[getCardPaymentAcceptPointAction] Unable to get cartState cart');
    }

    // Cart lines could be updated after copying the cart to checkout state. So need to check cart instead of checkoutcart.
    if (!cartState.cart.CartLines || !ArrayExtensions.hasElements(cartState.cart.CartLines)) {
        ctx.telemetry.exception(new Error('[getCardPaymentAcceptPointAction] Cart is empty'));
        throw new Error('[getCardPaymentAcceptPointAction] Cart is empty');
    }

    const requestUrl =
        typeof ctx.requestContext.url.requestUrl === 'string'
            ? new URL(ctx.requestContext.url.requestUrl)
            : ctx.requestContext.url.requestUrl;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Existing code.
    const origin = get(window, 'location.origin') || requestUrl.origin;

    const cardPaymentAcceptSettings = {
        '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.CardPaymentAcceptSettings',
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Any type.
        HostPageOrigin: origin,
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions, @typescript-eslint/no-unsafe-assignment -- Any type.
        AdaptorPath: `${origin}/Connectors/` || origin,
        CardPaymentEnabled: false,
        CardTokenizationEnabled: true,
        HideBillingAddress: true,
        ...(paymentTenderType && { TenderTypeId: paymentTenderType }),
        PaymentAmount: amountDue !== 0 ? amountDue : checkoutState.checkoutCart.cart.AmountDue ?? 0,
        RequireShippingAddress: true
    };

    return CartsDataActions.getCardPaymentAcceptPointAsync(
        { callerContext: ctx, bypassCache: 'get' },
        checkoutState.checkoutCart.cart.Id,
        cardPaymentAcceptSettings,
        []
    )
        .then(cardPaymentAcceptPoint => {
            if (!cardPaymentAcceptPoint.AcceptPageUrl && !cardPaymentAcceptPoint.AcceptPageContent) {
                throw new Error(
                    '[getCardPaymentAcceptPointAction]Payment Accept Page has neither return AcceptPageUrl nor AcceptPageContent in updatePaymentAcceptPageData'
                );
            }
            return cardPaymentAcceptPoint;
        })
        .catch(error => {
            ctx.telemetry.exception(error as Error);
            ctx.telemetry.debug('Unable to get Card Payment Accept Point');
            throw error;
        });
}

/**
 * Get card payment accept point.
 */
export const getCardPaymentAcceptPointActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce/checkout-express/get-card-payment-accept-point',
    action: getCardPaymentAcceptPointAction as IAction<CardPaymentAcceptPoint>,
    input: createCheckoutPaymentInstrumentInput
});

export default getCardPaymentAcceptPointActionDataAction;
